import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

/**
 * Portal switcher pill. Renders a "Signed in · ROLE ▾" trigger with a menu of
 * the views the current user can access. Non-admins only ever see their own
 * view, so we hide the switcher entirely for them (returning null keeps the
 * header layout stable across role changes).
 */
export default function SwitchViewMenu({ current, roleLabel }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const isAdmin = user && user.role === 'admin';

  // ⌘K / Ctrl+K toggles the switcher for admins. Ignore when the user is
  // typing in an input/textarea/contenteditable so we don't hijack their
  // keystrokes. Non-admins never see the menu, so the shortcut is a no-op.
  useEffect(() => {
    if (!isAdmin) return undefined;
    const handler = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      if (!isK || !(e.metaKey || e.ctrlKey)) return;
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return;
      e.preventDefault();
      setOpen((prev) => !prev);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isAdmin]);

  if (!user) return null;
  const roles = {
    admin:   { label: roleLabel || 'ADMIN',   tone: 'bg-[#5b93d6]' },
    advisor: { label: roleLabel || 'ADVISOR', tone: 'bg-blue-500' },
    csa:     { label: roleLabel || 'CSA',     tone: 'bg-emerald-500' },
  };
  const active = roles[current] || roles.admin;

  // CSAs & advisors only ever see one dashboard — no need for a switcher.
  if (!isAdmin) return null;

  const views = [
    { key: 'admin',   label: 'Master Admin',      path: '/admin/dashboard' },
    { key: 'advisor', label: 'My Advisor Portal', path: '/advisor/dashboard' },
    { key: 'csa',     label: 'My CSA Portal',     path: '/csa/dashboard' },
  ];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 rounded-md bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm text-white transition-colors"
          data-testid="switch-view-trigger"
          title="Switch view (⌘K / Ctrl+K)"
        >
          <span className="hidden sm:inline">{user.name || 'Signed in'}</span>
          <span className="sm:hidden">Menu</span>
          <span className="hidden md:inline text-white/60">·</span>
          <span className={`hidden md:inline text-xs uppercase tracking-wider ${active.tone} px-1.5 py-0.5 rounded font-medium`}>
            {active.label}
          </span>
          <ChevronDown className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="switch-view-menu">
        <DropdownMenuLabel className="text-xs text-[#94a3b8] font-normal flex items-center justify-between">
          <span>Switch view</span>
          <kbd className="rounded border border-[#e2e8f0] bg-[#f8fafc] px-1.5 py-0.5 text-[10px] font-mono text-[#475569]">⌘K</kbd>
        </DropdownMenuLabel>
        {views.map((v) => (
          <DropdownMenuItem key={v.key} onClick={() => navigate(v.path)} data-testid={`switch-view-${v.key}`}>
            {current === v.key
              ? <Check className="w-4 h-4 mr-2 text-[#5b93d6]" />
              : <span className="w-4 h-4 mr-2" />}
            <span className="flex-1">{v.label}</span>
            {current === v.key && (
              <span className="text-[10px] uppercase tracking-wider text-[#94a3b8]">Current</span>
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-[#94a3b8] font-normal">Signed in as</DropdownMenuLabel>
        <div className="px-2 pb-2 text-xs text-[#475569] truncate" data-testid="switch-view-signed-in-email">{user.email}</div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
